import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Pagination } from '../components/ui/Pagination';
import { Sheet } from '../components/ui/Sheet';
import { UserForm } from '../components/UserForm';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  role: string;
  role_id: number;
  client_id: number | null;
  establishment_id: number | null;
  is_active: number;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export const Users: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const fetchUsers = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      if (search) {
        params.append('search', search);
      }

      const route = `users/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (res.status === 401) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'No autorizado';
        setError(errorMessage);
        setLoading(false);
        return;
      }

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar usuarios';
        throw new Error(errorMessage);
      }

      setUsers(json.data?.users || []);
      setPagination(prev => ({
        ...prev,
        total: json.data?.pagination?.total || 0,
        pages: json.data?.pagination?.pages || 0
      }));
    } catch (err: any) {
      console.error('Error cargando usuarios:', err);
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token, pagination.page, search]);

  const handleCreate = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;

    try {
      const res = await apiFetch(`users/delete`, {
        method: 'POST',
        body: JSON.stringify({ id })
      }, token);

      if (isNetworkErrorResponse(res)) {
        alert('Error de conexión. Verifica tu conexión a internet.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al eliminar usuario';
        throw new Error(errorMessage);
      }

      fetchUsers();
    } catch (err: any) {
      alert(err.message || 'Error al eliminar usuario');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingUser(null);
    fetchUsers();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrador',
      management: 'Gestión',
      client: 'Cliente',
      establishment: 'Establecimiento'
    };
    return labels[role] || role;
  };

  const getRoleBadgeVariant = (role: string) => {
    const variants: Record<string, 'success' | 'info' | 'warning' | 'error'> = {
      admin: 'error',
      management: 'info',
      client: 'success',
      establishment: 'warning'
    };
    return variants[role] || 'info';
  };

  return (
    <DashboardLayout
      title="Gestión de Usuarios"
      subtitle="Administra usuarios, roles y permisos del sistema"
      showBackButton={true}
      backPath="/admin"
    >
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Create */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                <SearchBar
                  value={search}
                  onChange={(value) => {
                    setSearch(value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  placeholder="Buscar por nombre, email o usuario..."
                  className="flex-1 w-full sm:w-auto min-w-0"
                />
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCreate}
                  className="whitespace-nowrap w-full sm:w-auto"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">Nuevo Usuario</span>
                  <span className="sm:hidden">Nuevo</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Error Message */}
          {error && (
            <Alert variant="error">{error}</Alert>
          )}

          {/* Users Table */}
          {loading ? (
            <Card>
              <CardContent>
                <div className="p-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                  <p className="text-gray-600">Cargando usuarios...</p>
                </div>
              </CardContent>
            </Card>
          ) : users.length === 0 ? (
            <Card>
              <CardContent>
                <div className="p-12 text-center">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay usuarios</h3>
                  <p className="text-gray-600 mb-6">Comienza creando tu primer usuario del sistema.</p>
                  <Button variant="primary" onClick={handleCreate}>
                    Crear Primer Usuario
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableHeaderCell>Nombre</TableHeaderCell>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Usuario</TableHeaderCell>
                        <TableHeaderCell>Rol</TableHeaderCell>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell>Último Acceso</TableHeaderCell>
                        <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div className="font-medium text-gray-900">
                                {user.first_name} {user.last_name}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">{user.email}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">{user.username}</div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getRoleBadgeVariant(user.role)}>
                                {getRoleLabel(user.role)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={user.is_active ? 'success' : 'error'}>
                                {user.is_active ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">
                                {user.last_login 
                                  ? new Date(user.last_login).toLocaleDateString('es-MX', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'Nunca'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(user)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1.5 sm:p-2"
                                >
                                  <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  <span className="hidden sm:inline">Editar</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(user.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 sm:p-2"
                                >
                                  <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  <span className="hidden sm:inline">Eliminar</span>
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="mt-6">
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                  />
                </div>
              )}
            </>
          )}

          {/* Form Sheet */}
          <Sheet
            isOpen={showForm}
            onClose={handleFormCancel}
            title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            subtitle={editingUser ? 'Modifica la información del usuario' : 'Completa los datos para crear un nuevo usuario'}
          >
            <UserForm
              user={editingUser}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </Sheet>
        </div>
      </div>
    </DashboardLayout>
  );
};

